import { inject, Injectable } from '@angular/core';
import { GedService } from './ged.service';
import {
  GetDemandeAffiliationFileUrlGQL,
  GetDemandeHabilitationFileUrlGQL,
  GetDemandeValidationPjFileUrlGQL,
} from '@graphql/generated';
import { lastValueFrom } from 'rxjs';
import { FileUrl } from './models/file-url.model';
import { DocumentModel } from './document.model';
import { DemandeType } from '@lib/models/non-conformite/demande-type.model';

@Injectable({ providedIn: 'root' })
export class AzureGedService extends GedService {
  private readonly getDemandeAffiliationFileUrlGql = inject(
    GetDemandeAffiliationFileUrlGQL
  );
  private readonly getDemandeValidationPjFileUrlGql = inject(
    GetDemandeValidationPjFileUrlGQL
  );
  private readonly getDemandeHabilitationFileUrlGql = inject(
    GetDemandeHabilitationFileUrlGQL
  );

  private readonly fileUrls: FileUrl[] = [];

  public override async getDemandeAffiliationFileUrl(
    gedId: string,
    isRetourNc = false
  ): Promise<string> {
    const queryResult = await lastValueFrom(
      this.getDemandeAffiliationFileUrlGql.fetch({
        variables: { gedId: gedId, isRetourNc: isRetourNc },
      })
    );

    return queryResult.data!.demandeAffiliationFileUrl;
  }

  public override async getDemandeValidationPjFileUrl(
    gedId: string,
    isRetourNc = false
  ): Promise<string> {
    const queryResult = await lastValueFrom(
      this.getDemandeValidationPjFileUrlGql.fetch({
        variables: {
          gedId: gedId,
          isRetourNc: isRetourNc,
        },
      })
    );

    return queryResult.data!.demandeValidationPjFileUrl;
  }

  public override async getDemandeHabilitationFileUrl(
    gedId: string,
    isRetourNc = false,
    demandeHabilitationPublicId?: string
  ): Promise<string> {
    const queryResult = await lastValueFrom(
      this.getDemandeHabilitationFileUrlGql.fetch({
        variables: {
          gedId: gedId,
          isRetourNc: isRetourNc,
          demandeHabilitationPublicId,
        },
      })
    );

    return queryResult.data!.demandeHabilitationFileUrl;
  }

  public override uploadFile(file: File, uploadUri: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUri, true);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Erreur lors de l'upload : ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Erreur réseau'));

      xhr.send(file);
    });
  }

  public async getDocumentFileUrls(
    document: DocumentModel,
    serviceType: DemandeType = 'affiliation',
    demandeHabilitationPublicId?: string
  ): Promise<FileUrl[]> {
    const isRetourNc = (document.retourNcs?.[0]?.files?.length ?? 0) > 0;

    const files = isRetourNc ? document.retourNcs?.[0].files : document.files;

    if (!files) return [];

    const result: FileUrl[] = [];

    for (const f of files) {
      const existingUrl = this.fileUrls.find(
        fu =>
          (fu.fileModel.gedId && fu.fileModel.gedId === f.gedId) ||
          (fu.isLocal && fu.fileModel.file && fu.fileModel.file === f.file)
      );

      if (existingUrl) {
        result.push(existingUrl);
      } else {
        const url = f.gedId
          ? await this.getFileUrlByServiceType(
              serviceType,
              f.gedId,
              isRetourNc,
              demandeHabilitationPublicId
            )
          : URL.createObjectURL(f.file!);

        const fileUrl = {
          fileModel: f,
          url: url,
          isLocal: !!f.gedId,
        };

        result.push(fileUrl);
        this.fileUrls.push(fileUrl);
      }
    }

    return result;
  }

  private getFileUrlByServiceType(
    type: DemandeType,
    gedId: string,
    isRetourNc: boolean,
    demandeHabilitationPublicId?: string
  ): Promise<string> {
    switch (type) {
      case 'affiliation':
        return this.getDemandeAffiliationFileUrl(gedId, isRetourNc);
      case 'validationPj':
        return this.getDemandeValidationPjFileUrl(gedId, isRetourNc);
      case 'habilitation':
        return this.getDemandeHabilitationFileUrl(
          gedId,
          isRetourNc,
          demandeHabilitationPublicId
        );
    }
  }
}
